/**
 * YBM Studios — Lead Character Library
 * --------------------------------------------------------------
 * 20 AI-generated, business-promo-ready character portraits for use as
 * the "lead" in commercials, brand spots and product launches.
 *
 * Portraits are produced once by `backend/app/scripts/generate_characters.py`
 * via Gemini Nano Banana and stored under `backend/static/characters/<id>.png`.
 * The frontend reads them through `${BACKEND_URL}/api/static/characters/{id}.png`
 * so there is no external CDN dependency (Unsplash was unreliable on mobile
 * carriers — that surfaced as broken thumbs in the founder's screenshot).
 *
 * Use `characterThumb(BACKEND_URL, id)` to build the full URL at render time.
 */
import { BACKEND_URL } from "@/lib/api";

export function characterThumb(id) {
  return `${BACKEND_URL}/api/static/characters/${id}.webp`;
}

export const LEAD_CHARACTERS = [
  {
    id: "andre",
    name: "Andre",
    description: "young African-American businessman, mid-20s, charcoal tailored suit, gold pocket-square, confident stance",
    vibe: "Founder / CEO",
  },
  {
    id: "maya",
    name: "Maya",
    description: "Black female creative director, late-20s, cream silk blouse, gold hoop earrings, natural curls",
    vibe: "Creative Director",
  },
  {
    id: "darius",
    name: "Darius",
    description: "Black male athlete-entrepreneur, early-30s, black turtleneck and bomber, clean fade, gold chain",
    vibe: "Athlete / Brand",
  },
  {
    id: "imani",
    name: "Imani",
    description: "Black female luxury model, mid-20s, ivory long coat, sleek braided updo, soft beauty lighting",
    vibe: "Luxury / Fashion",
  },
  {
    id: "marcus",
    name: "Marcus",
    description: "African-American tech founder, 30s, black hoodie, designer glasses, beard, modern office backdrop",
    vibe: "Tech Founder",
  },
  {
    id: "zara",
    name: "Zara",
    description: "Black female podcast host, late-20s, beige knit and gold layered necklaces, locs pulled back",
    vibe: "Host / Creator",
  },
  {
    id: "kwame",
    name: "Kwame",
    description: "Black male music artist, mid-20s, black leather jacket, diamond stud, intense studio light",
    vibe: "Music / Artist",
  },
  {
    id: "amara",
    name: "Amara",
    description: "Black female wellness founder, 30s, oat-color linen set, soft minimal makeup, warm studio glow",
    vibe: "Wellness / DTC",
  },
  {
    id: "elena",
    name: "Elena",
    description: "Latina luxury entrepreneur, 30s, espresso-tone hair, structured ivory blazer, gold watch",
    vibe: "Luxury / Hospitality",
  },
  {
    id: "rashid",
    name: "Rashid",
    description: "Middle-Eastern male luxury watch model, mid-30s, navy double-breasted suit, beard, urban skyline",
    vibe: "Luxury / Watches",
  },
  {
    id: "naomi",
    name: "Naomi",
    description: "Mixed-race female beauty model, mid-20s, dewy skin, silk slip dress, golden-hour light",
    vibe: "Beauty / Skincare",
  },
  {
    id: "trey",
    name: "Trey",
    description: "Young Black male athlete, early-20s, performance hoodie, sweat on brow, gym lighting",
    vibe: "Fitness / Apparel",
  },
  {
    id: "sade",
    name: "Sade",
    description: "Black female fashion designer, 30s, tailored black jumpsuit, gold septum, color-block studio",
    vibe: "Fashion Designer",
  },
  {
    id: "noah",
    name: "Noah",
    description: "Black male barista / hospitality lead, mid-20s, denim apron, easy smile, warm cafe lighting",
    vibe: "Hospitality / F&B",
  },
  {
    id: "leila",
    name: "Leila",
    description: "Black female realtor / luxury home spokesperson, 30s, ivory blazer, pearl earrings, modern foyer",
    vibe: "Real Estate",
  },
  {
    id: "jaden",
    name: "Jaden",
    description: "Young Black male skater / streetwear model, 20s, oversized vintage tee, gold grills, alley setting",
    vibe: "Streetwear",
  },
  {
    id: "iyana",
    name: "Iyana",
    description: "Black female chef / food spokesperson, 30s, crisp white chef coat, ring light, kitchen pass background",
    vibe: "Food / Restaurant",
  },
  {
    id: "calvin",
    name: "Calvin",
    description: "Black senior executive, 50s, salt-and-pepper hair, navy three-piece suit, walking through corporate lobby",
    vibe: "Executive / Finance",
  },
  {
    id: "kira",
    name: "Kira",
    description: "Black female fitness / activewear spokesperson, late-20s, matte black two-piece set, rooftop sunrise",
    vibe: "Activewear",
  },
  {
    id: "miles",
    name: "Miles",
    description: "Young Black male DJ / nightlife creator, mid-20s, all-black fit, headphones on neck, neon haze",
    vibe: "Nightlife / Music",
  },
];

export function findCharacter(id) {
  return LEAD_CHARACTERS.find((c) => c.id === id) || null;
}
